import { useQuery } from '@tanstack/react-query';

const BASE = '/api/tick-lake';
const STALE = 5 * 60_000;
// Required by the production edge (nginx/Cloudflare) in front of /api/tick-lake;
// without it the gateway returns 403 {"message":"Forbidden"}. Injected at build
// time via REACT_APP_API_KEY (see .github/workflows/frontend-deploy.yml).
const API_KEY = process.env.REACT_APP_API_KEY || '';

async function fetchJson(path) {
    const res = await fetch(`${BASE}${path}`, {
        headers: { 'x-api-key': API_KEY },
    });
    if (!res.ok) {
        const text = await res.text();
        throw new Error(`${res.status}: ${text}`);
    }
    return res.json();
}

export function useTickTelemetry() {
    return useQuery({
        queryKey: ['tick-lake', 'telemetry'],
        queryFn: () => fetchJson('/telemetry'),
        staleTime: STALE,
        refetchInterval: 60_000,
    });
}

export function useTickDates() {
    return useQuery({
        queryKey: ['tick-lake', 'dates'],
        queryFn: () => fetchJson('/dates'),
        staleTime: STALE,
    });
}

export function useTickSymbols(date, search) {
    // The lake holds ~14k instruments per day, so an unfiltered list is useless
    // (the first N are all one underlying's strikes). Keep it search-driven:
    // only query once the user types, and pull a wide limit so matches aren't cut.
    const term = (search || '').trim();
    return useQuery({
        queryKey: ['tick-lake', 'symbols', date, term],
        queryFn: () =>
            fetchJson(`/symbols/${date}?search=${encodeURIComponent(term)}&limit=1000`),
        staleTime: STALE,
        enabled: !!date && term.length >= 1,
    });
}

export function useTickSeries(symbol, date, tf, rth) {
    return useQuery({
        queryKey: ['tick-lake', 'series', symbol, date, tf, rth],
        queryFn: () =>
            fetchJson(`/series/${encodeURIComponent(symbol)}/${date}?tf=${tf}&rth=${rth}`),
        staleTime: STALE,
        enabled: !!symbol && !!date,
    });
}

export function useTickBacktest(symbol, date, threshold, slippageBps, orderQty, rth, enabled) {
    return useQuery({
        queryKey: ['tick-lake', 'backtest', symbol, date, threshold, slippageBps, orderQty, rth],
        queryFn: () =>
            fetchJson(
                `/backtest/${encodeURIComponent(symbol)}/${date}` +
                    `?threshold=${threshold}&slippage_bps=${slippageBps}` +
                    `&order_qty=${orderQty}&rth=${rth}`,
            ),
        staleTime: STALE,
        enabled: !!symbol && !!date && !!enabled,
    });
}

export function useTickTicks(symbol, date, offset, limit = 500) {
    return useQuery({
        queryKey: ['tick-lake', 'ticks', symbol, date, offset, limit],
        queryFn: () =>
            fetchJson(
                `/ticks/${encodeURIComponent(symbol)}/${date}?limit=${limit}&offset=${offset}`,
            ),
        staleTime: STALE,
        enabled: !!symbol && !!date,
    });
}

export const TIMEFRAMES = ['1m', '2m', '5m', '15m'];
